import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Form, Modal, Space, Switch, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { CloseOutlined, DeleteOutlined, EditOutlined, EyeOutlined, NotificationOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@stores/auth.store';
import Table from '@/components/ui/table/Table';
import { AppModal } from '@/components/ui/AppModal';
import { AppInput, AppTextArea } from '@/components/ui/AppInput';
import { AppSelect } from '@/components/ui/AppSelect';
import { Button } from '@/components/ui/Button';
import { AppContextMenu } from '@/components/ui/AppContextMenu';
import { useNotices } from '@hooks/tenant/useNotices';
import { useCreateNotice } from '@hooks/tenant/useCreateNotice';
import { useUpdateNotice } from '@hooks/tenant/useUpdateNotice';
import { useDeleteNotice } from '@hooks/tenant/useDeleteNotice';
import { sendNoticeNotificationNow } from '@services/registry';
import {
    DashboardContainer,
    StudentsHeaderActions,
    StudentsHeaderLeft,
    StudentsHeaderRow,
    StudentsHeaderSub,
    StudentsHeaderTitle,
} from './style';

const { Text } = Typography;
const AUDIENCE_OPTIONS = [
    { value: 'all', label: 'All' },
    { value: 'all_teacher', label: 'All Teacher' },
    { value: 'all_staff', label: 'All Staff' },
    { value: 'all_student', label: 'All Student' },
    { value: 'selected_teacher', label: 'Selected Teacher' },
    { value: 'selected_staff', label: 'Selected Staff' },
    { value: 'selected_student', label: 'Selected Student' },
];

const TYPE_OPTIONS = [
    { value: 'announcement', label: 'Announcement' },
    { value: 'notice', label: 'Notice' },
];

const AUDIENCE_LABEL_BY_TYPE = Object.fromEntries(AUDIENCE_OPTIONS.map((item) => [item.value, item.label]));
const ROLE_BY_AUDIENCE_TYPE = {
    all_teacher: 'teacher',
    all_staff: 'staff',
    all_student: 'student',
    selected_teacher: 'teacher',
    selected_staff: 'staff',
    selected_student: 'student',
};

const ALL_TYPE_BY_ROLE = {
    teacher: 'all_teacher',
    staff: 'all_staff',
    student: 'all_student',
};

const SELECTED_TYPE_BY_ROLE = {
    teacher: 'selected_teacher',
    staff: 'selected_staff',
    student: 'selected_student',
};

function normalizeScheduleDates(values) {
    const uniqueByDay = new Map();
    (values || []).forEach((value) => {
        const normalizedValue = typeof value?.toDate === 'function' ? value.toDate() : value;
        const parsed = dayjs(normalizedValue);
        if (!parsed.isValid()) return;
        const key = parsed.format('YYYY-MM-DD');
        if (!uniqueByDay.has(key)) uniqueByDay.set(key, parsed.startOf('day'));
    });
    return Array.from(uniqueByDay.values()).sort((a, b) => a.valueOf() - b.valueOf());
}

function formatDate(value) {
    if (!value) return '—';
    if (typeof value?.toDate === 'function') return dayjs(value.toDate()).format('DD MMM YYYY');
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : '—';
}

function formatDateTime(value) {
    if (!value) return '—';
    if (typeof value?.toDate === 'function') return dayjs(value.toDate()).format('DD MMM YYYY, hh:mm A');
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD MMM YYYY, hh:mm A') : '—';
}

function resolveNoticeStatus(row) {
    const publishRaw = row?.publishAt?.toDate?.() || row?.publishAt || null;
    const publishAt = publishRaw ? dayjs(publishRaw) : null;
    if (row?.isNotification) return { label: 'Sent', color: 'green' };
    if (publishAt && publishAt.isAfter(dayjs())) return { label: 'Scheduled', color: 'gold' };
    return { label: 'Pending', color: 'default' };
}

function normalizeLegacyAudience(row) {
    if (!row) return [];
    if (Array.isArray(row.audiences) && row.audiences.length) return row.audiences;
    if (row.audienceType === 'all') return [{ type: 'all', userIds: [] }];
    if (row.audienceType === 'roles') {
        return (row.targetRoles || []).map((role) => {
            if (role === 'teacher') return { type: 'all_teacher', userIds: [] };
            if (role === 'staff') return { type: 'all_staff', userIds: [] };
            return { type: 'all_student', userIds: [] };
        });
    }
    if (row.audienceType === 'individual') {
        return [{ type: 'selected_student', userIds: row.targetUserIds || [] }];
    }
    return [];
}

function buildAudienceSummary(audiences, audienceUsers) {
    if (!Array.isArray(audiences) || audiences.length === 0) return '—';
    return audiences
        .map((entry) => {
            const label = AUDIENCE_LABEL_BY_TYPE[entry.type] || entry.type;
            if (!entry.type.startsWith('selected_')) return label;
            const names = (entry.userIds || [])
                .map((id) => audienceUsers.find((x) => x.id === id)?.label)
                .filter(Boolean);
            return names.length ? `${label}: ${names.join(', ')}` : `${label}: 0 selected`;
        })
        .join(' | ');
}

const NoticesAnnouncements = () => {
    const [searchText, setSearchText] = useState('');
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState('create');
    const [editingRecord, setEditingRecord] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [scheduleDatePicker, setScheduleDatePicker] = useState(null);
    const [audienceTypePicker, setAudienceTypePicker] = useState(null);
    const [selectedAudienceUserIds, setSelectedAudienceUserIds] = useState([]);
    const [selectedAudienceUserPicker, setSelectedAudienceUserPicker] = useState(null);
    const [hoveredAudienceChipId, setHoveredAudienceChipId] = useState(null);
    const [sendingNotificationId, setSendingNotificationId] = useState(null);
    const [rowMenu, setRowMenu] = useState(null);
    const [menuActionLoading, setMenuActionLoading] = useState(null);
    const [form] = Form.useForm();
    const queryClient = useQueryClient();
    const isViewMode = mode === 'view';
    const isEditMode = mode === 'edit';
    const isCreateMode = mode === 'create';

    const tenantId = useAuthStore((s) => s.user?.tenantId);
    const user = useAuthStore((s) => s.user);
    const { rows, total, analytics, audienceUsers, loading } = useNotices({
        tenantId,
        page: currentPage,
        pageSize: currentPageSize,
    });
    const createMutation = useCreateNotice({ tenantId });
    const updateMutation = useUpdateNotice({ tenantId });
    const deleteMutation = useDeleteNotice({ tenantId });
    const audiencesWatch = Form.useWatch('audiences', form) || [];
    const scheduleDatesWatch = Form.useWatch('scheduleDates', form) || [];
    const sendImmediatelyWatch = Form.useWatch('sendImmediately', form);
    const sendNotificationMutation = useMutation({
        mutationFn: async ({ noticeId, noticeType, force = false, source = 'manual', markAsSent = true }) => {
            return await sendNoticeNotificationNow({
                tenantId,
                noticeId,
                noticeType,
                force,
                source,
                markAsSent,
                actor: {
                    uid: user?.uid || null,
                    email: user?.email || null,
                    name: user?.name || null,
                },
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['notices', tenantId] });
        },
    });

    const filteredRows = useMemo(() => {
        const q = (searchText || '').trim().toLowerCase();
        if (!q) return rows;
        return rows.filter((row) => {
            const title = String(row.title || '').toLowerCase();
            const body = String(row.body || '').toLowerCase();
            const audience = buildAudienceSummary(normalizeLegacyAudience(row), audienceUsers).toLowerCase();
            return title.includes(q) || body.includes(q) || audience.includes(q);
        });
    }, [rows, searchText, audienceUsers]);

    const audienceUserOptions = useMemo(() => {
        return (audienceUsers || []).map((item) => ({
            value: item.id,
            label: `${item.label} (${item.role})`,
        }));
    }, [audienceUsers]);

    const roleUserIdsMap = useMemo(() => {
        const map = { teacher: [], staff: [], student: [] };
        (audienceUsers || []).forEach((entry) => {
            if (map[entry.role]) map[entry.role].push(entry.id);
        });
        return map;
    }, [audienceUsers]);

    const selectedAudienceEntryByRole = useMemo(() => {
        const map = { teacher: null, staff: null, student: null };
        (audiencesWatch || []).forEach((entry) => {
            const role = ROLE_BY_AUDIENCE_TYPE[entry?.type];
            if (!role || !entry.type.startsWith('selected_')) return;
            map[role] = entry;
        });
        return map;
    }, [audiencesWatch]);

    const availableAudienceOptions = useMemo(() => {
        const activeTypes = new Set((audiencesWatch || []).map((entry) => entry.type));
        return AUDIENCE_OPTIONS.filter((option) => {
            const type = option.value;
            if (type === 'all') return !activeTypes.has('all');
            const role = ROLE_BY_AUDIENCE_TYPE[type];
            if (!role) return !activeTypes.has(type);

            const roleUserIds = roleUserIdsMap[role] || [];
            if (!roleUserIds.length) return false;

            if (type.startsWith('all_')) {
                return !activeTypes.has(type);
            }

            const allType = ALL_TYPE_BY_ROLE[role];
            if (activeTypes.has(allType)) return false;

            const selectedIds = selectedAudienceEntryByRole[role]?.userIds || [];
            const remainingIds = roleUserIds.filter((id) => !selectedIds.includes(id));
            return remainingIds.length > 0;
        });
    }, [audiencesWatch, roleUserIdsMap, selectedAudienceEntryByRole]);

    const filteredAudienceUserOptions = useMemo(() => {
        if (!audienceTypePicker?.startsWith('selected_')) return [];
        const role = audienceTypePicker.replace('selected_', '');
        const alreadyAddedRoleIds = selectedAudienceEntryByRole[role]?.userIds || [];
        return audienceUserOptions.filter((option) => {
            const user = (audienceUsers || []).find((x) => x.id === option.value);
            return (
                user?.role === role
                && !alreadyAddedRoleIds.includes(option.value)
                && !selectedAudienceUserIds.includes(option.value)
            );
        });
    }, [audienceTypePicker, audienceUserOptions, audienceUsers, selectedAudienceUserIds, selectedAudienceEntryByRole]);

    const selectedAudienceUserChips = useMemo(() => {
        return selectedAudienceUserIds
            .map((id) => {
                const user = (audienceUsers || []).find((entry) => entry.id === id);
                if (!user) return null;
                return { id, label: user.label };
            })
            .filter(Boolean);
    }, [selectedAudienceUserIds, audienceUsers]);

    const selectedAudienceChipTitle = useMemo(() => {
        if (audienceTypePicker === 'selected_teacher') return 'Teachers';
        if (audienceTypePicker === 'selected_staff') return 'Staff Members';
        if (audienceTypePicker === 'selected_student') return 'Students';
        return 'Selected Users';
    }, [audienceTypePicker]);

    const addAudience = () => {
        if (!audienceTypePicker) {
            message.warning('Please choose target audience');
            return;
        }
        if (audienceTypePicker.startsWith('selected_') && selectedAudienceUserIds.length === 0) {
            message.warning('Please select at least one user');
            return;
        }
        const current = form.getFieldValue('audiences') || [];
        const role = ROLE_BY_AUDIENCE_TYPE[audienceTypePicker];
        let nextAudiences = [...current];

        if (audienceTypePicker === 'all') {
            nextAudiences = [{ type: 'all', userIds: [] }];
        } else if (audienceTypePicker.startsWith('all_') && role) {
            const allRoleIds = roleUserIdsMap[role] || [];
            const selectedType = SELECTED_TYPE_BY_ROLE[role];
            nextAudiences = current.filter((entry) => entry.type !== selectedType && entry.type !== audienceTypePicker);
            nextAudiences.push({ type: audienceTypePicker, userIds: allRoleIds });
        } else if (audienceTypePicker.startsWith('selected_') && role) {
            const existing = current.find((entry) => entry.type === audienceTypePicker);
            const mergedUserIds = Array.from(new Set([...(existing?.userIds || []), ...selectedAudienceUserIds]));
            nextAudiences = current.filter((entry) => entry.type !== audienceTypePicker);
            nextAudiences.push({ type: audienceTypePicker, userIds: mergedUserIds });
        } else {
            if (current.some((entry) => entry.type === audienceTypePicker)) {
                message.info('This audience is already added');
                return;
            }
            nextAudiences.push({ type: audienceTypePicker, userIds: [] });
        }

        form.setFieldValue('audiences', nextAudiences);
        setAudienceTypePicker(null);
        setSelectedAudienceUserIds([]);
        setSelectedAudienceUserPicker(null);
        setHoveredAudienceChipId(null);
    };

    const removeAudience = (index) => {
        const current = form.getFieldValue('audiences') || [];
        form.setFieldValue('audiences', current.filter((_, i) => i !== index));
    };

    const resetModal = () => {
        setModalOpen(false);
        setMode('create');
        setEditingRecord(null);
        setScheduleDatePicker(null);
        setAudienceTypePicker(null);
        setSelectedAudienceUserIds([]);
        setSelectedAudienceUserPicker(null);
        setHoveredAudienceChipId(null);
        form.resetFields();
    };

    const openCreate = () => {
        setMode('create');
        setEditingRecord(null);
        form.resetFields();
        form.setFieldsValue({
            type: 'announcement',
            audiences: [],
            active: true,
            sendImmediately: true,
            isNotification: true,
            publishAt: dayjs(),
            scheduleDates: [],
        });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setMode('edit');
        setEditingRecord(record);
        form.resetFields();
        const normalizedAudiences = normalizeLegacyAudience(record);
        form.setFieldsValue({
            ...record,
            publishAt: record?.publishAt ? dayjs(record.publishAt?.toDate?.() || record.publishAt) : null,
            scheduleDates: normalizeScheduleDates(record?.scheduleDates || (record?.publishAt ? [record.publishAt] : [])),
            audiences: normalizedAudiences,
            active: typeof record?.active === 'boolean' ? record.active : true,
            type: record?.type || 'announcement',
            sendImmediately: !!record?.isNotification,
        });
        setModalOpen(true);
    };

    const openView = (record) => {
        setMode('view');
        setEditingRecord(record);
        form.resetFields();
        const normalizedAudiences = normalizeLegacyAudience(record);
        form.setFieldsValue({
            ...record,
            publishAt: record?.publishAt ? dayjs(record.publishAt?.toDate?.() || record.publishAt) : null,
            scheduleDates: normalizeScheduleDates(record?.scheduleDates || (record?.publishAt ? [record.publishAt] : [])),
            audiences: normalizedAudiences,
            active: typeof record?.active === 'boolean' ? record.active : true,
            type: record?.type || 'announcement',
            sendImmediately: !!record?.isNotification,
        });
        setModalOpen(true);
    };

    const handleDelete = async (row) => {
        try {
            await deleteMutation.mutateAsync({ noticeId: row.id, noticeType: row.noticeType || row.type });
            message.success('Notice deleted');
        } catch (error) {
            message.error(error?.message || 'Failed to delete notice');
        }
    };

    useEffect(() => {
        if (!rowMenu) return;
        const closeMenu = () => setRowMenu(null);
        window.addEventListener('click', closeMenu);
        window.addEventListener('scroll', closeMenu, true);
        window.addEventListener('resize', closeMenu);
        return () => {
            window.removeEventListener('click', closeMenu);
            window.removeEventListener('scroll', closeMenu, true);
            window.removeEventListener('resize', closeMenu);
        };
    }, [rowMenu]);

    useEffect(() => {
        if (!rowMenu) {
            setMenuActionLoading(null);
        }
    }, [rowMenu]);

    useEffect(() => {
        if (sendImmediatelyWatch) {
            form.setFieldValue('publishAt', dayjs());
            form.setFieldValue('scheduleDates', []);
        }
    }, [sendImmediatelyWatch, form]);

    const handleSendNotification = async (row) => {
        if (!row?.id) return;
        const rowStatus = resolveNoticeStatus(row).label;
        const isReminder = rowStatus === 'Sent';
        let markAsSent = true;
        if (rowStatus === 'Scheduled') {
            markAsSent = await new Promise((resolve) => {
                Modal.confirm({
                    title: 'Remove schedule after sending?',
                    content: 'Yes = send now and mark as Sent. No = send now but keep Scheduled status.',
                    okText: 'Yes, remove schedule',
                    cancelText: 'No, keep schedule',
                    closable: false,
                    maskClosable: false,
                    onOk: () => resolve(true),
                    onCancel: () => resolve(false),
                });
            });
        }
        setSendingNotificationId(row.id);
        try {
            const res = await sendNotificationMutation.mutateAsync({
                noticeId: row.id,
                noticeType: row.noticeType || row.type,
                force: isReminder,
                markAsSent,
                source: isReminder
                    ? 'reminder'
                    : (rowStatus === 'Scheduled' && !markAsSent ? 'manual_keep_schedule' : 'manual'),
            });
            if (res?.alreadySent) {
                message.info('Notification already sent');
            } else {
                if (rowStatus === 'Scheduled' && !markAsSent) {
                    message.success('Notification sent. Schedule kept.');
                } else {
                    message.success(isReminder ? 'Reminder sent successfully' : 'Notification sent successfully');
                }
            }
        } catch (error) {
            message.error(error?.message || 'Failed to send notification');
        } finally {
            setSendingNotificationId(null);
        }
    };

    const columns = useMemo(() => ([
        {
            title: 'Type',
            key: 'type',
            render: (_, row) => (
                <Tag color={row.type === 'notice' ? 'orange' : 'blue'}>
                    {(row.type || 'announcement').toUpperCase()}
                </Tag>
            ),
        },
        {
            title: 'Title',
            dataIndex: 'title',
            key: 'title',
            render: (value) => value || '—',
        },
        {
            title: 'Audience',
            key: 'audience',
            render: (_, row) => buildAudienceSummary(normalizeLegacyAudience(row), audienceUsers),
        },
        {
            title: 'Publish Date',
            key: 'publishAt',
            render: (_, row) => formatDate(row.publishAt),
        },
        {
            title: 'Notification Date',
            key: 'notifiedAt',
            render: (_, row) => formatDateTime(row.notifiedAt),
        },
        {
            title: 'Status',
            key: 'status',
            render: (_, row) => {
                const status = resolveNoticeStatus(row);
                return <Tag color={status.color}>{status.label}</Tag>;
            },
        },
    ]), [audienceUsers]);

    const notificationHistoryRows = useMemo(() => {
        if (!editingRecord) return [];
        const history = Array.isArray(editingRecord.notificationHistory) ? [...editingRecord.notificationHistory] : [];
        if (!history.length && editingRecord?.notifiedAt) {
            history.push({
                sentAt: editingRecord.notifiedAt,
                source: 'legacy',
                type: editingRecord?.type || 'announcement',
                actor: editingRecord?.createdBy || null,
            });
        }
        return history
            .map((entry, idx) => ({
                key: `${entry?.sentAt?.seconds || ''}-${idx}`,
                sentAtRaw: entry?.sentAt?.toDate?.() || entry?.sentAt || null,
                when: formatDateTime(entry?.sentAt),
                by: entry?.actor?.name || entry?.actor?.email || entry?.actor?.uid || 'System',
                type: (entry?.type || editingRecord?.type || 'announcement').toUpperCase(),
                source: (entry?.source || 'manual').replaceAll('_', ' '),
            }))
            .sort((a, b) => dayjs(b.sentAtRaw).valueOf() - dayjs(a.sentAtRaw).valueOf());
    }, [editingRecord]);

    const detailScheduleDates = useMemo(() => {
        if (!editingRecord) return [];
        const raw = Array.isArray(editingRecord.scheduleDates) && editingRecord.scheduleDates.length
            ? editingRecord.scheduleDates
            : (editingRecord.publishAt ? [editingRecord.publishAt] : []);
        return normalizeScheduleDates(raw);
    }, [editingRecord]);

    const handleSubmit = async () => {
        let values;
        try {
            values = await form.validateFields();
        } catch (error) {
            // AntD form validation errors are expected and already shown in UI.
            return;
        }
        const audiences = values.audiences || [];
        if (!audiences.length) {
            message.warning('Please add at least one target audience');
            return;
        }
        const audienceDetailRows = audiences.map((entry) => {
            const selectedRows = (audienceUsers || []).filter((userRow) => (entry.userIds || []).includes(userRow.id));
            return {
                ...entry,
                users: selectedRows,
            };
        });
        const nextIsNotification = isCreateMode ? !!values.sendImmediately : !!editingRecord?.isNotification;
        const normalizedScheduleDates = normalizeScheduleDates(
            values.scheduleDates
                || editingRecord?.scheduleDates
                || (editingRecord?.publishAt ? [editingRecord.publishAt] : [])
        );
        const earliestScheduleDate = normalizedScheduleDates.length ? normalizedScheduleDates[0].toDate() : null;
        const payload = {
            type: values.type,
            title: values.title,
            body: values.body,
            audiences,
            audienceDetails: audienceDetailRows,
            active: typeof values.active === 'boolean' ? values.active : true,
            publishAt: isCreateMode && values.sendImmediately
                ? dayjs().toDate()
                : earliestScheduleDate,
            scheduleDates: normalizedScheduleDates.map((d) => d.toDate()),
            isNotification: nextIsNotification,
        };

        try {
            if (mode === 'create') {
                await createMutation.mutateAsync({
                    payload,
                    createdBy: {
                        uid: user?.uid || null,
                        email: user?.email || null,
                        name: user?.name || null,
                    },
                });
                message.success('Notice created successfully');
            } else {
                await updateMutation.mutateAsync({
                    noticeId: editingRecord?.id,
                    noticeType: editingRecord?.noticeType || editingRecord?.type,
                    patch: payload,
                });
                if (payload.isNotification && !editingRecord?.isNotification && editingRecord?.id) {
                    await sendNoticeNotificationNow({
                        tenantId,
                        noticeId: editingRecord.id,
                        noticeType: editingRecord?.noticeType || editingRecord?.type,
                        source: 'edit_immediate',
                        actor: {
                            uid: user?.uid || null,
                            email: user?.email || null,
                            name: user?.name || null,
                        },
                    });
                }
                message.success('Notice updated successfully');
            }
            resetModal();
        } catch (error) {
            message.error(error?.message || 'Failed to save notice');
        }
    };

    return (
        <DashboardContainer>
            <StudentsHeaderRow>
                <StudentsHeaderLeft>
                    <StudentsHeaderTitle>Notices & Announcements</StudentsHeaderTitle>
                    <StudentsHeaderSub>
                        Manage communication professionally for all users, selected roles, or specific individuals.
                    </StudentsHeaderSub>
                </StudentsHeaderLeft>
                <StudentsHeaderActions>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                        Create Notice
                    </Button>
                </StudentsHeaderActions>
            </StudentsHeaderRow>

            <Table
                title="Communication Center"
                columns={columns}
                dataSource={filteredRows}
                loading={loading}
                searchValue={searchText}
                onSearchChange={setSearchText}
                showFilter={false}
                pagination={true}
                pageSize={currentPageSize}
                serverPagination
                currentPage={currentPage}
                totalItems={searchText ? filteredRows.length : total}
                onPageChange={(page, size) => {
                    setCurrentPage(page);
                    if (size && size !== currentPageSize) {
                        setCurrentPageSize(size);
                        setCurrentPage(1);
                    }
                }}
                showSearch={true}
                showPagination={true}
                onRowClick={openView}
                getRowProps={(record) => ({
                    onContextMenu: (event) => {
                        event.preventDefault();
                        setRowMenu({
                            record,
                            x: event.clientX,
                            y: event.clientY,
                        });
                    },
                })}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                <Tag color="blue">Notice: {analytics?.byType?.notice || 0}</Tag>
                <Tag color="geekblue">Announcement: {analytics?.byType?.announcement || 0}</Tag>
                <Tag color="default">Pending: {analytics?.byStatus?.pending || 0}</Tag>
                <Tag color="gold">Scheduled: {analytics?.byStatus?.scheduled || 0}</Tag>
                <Tag color="green">Sent: {analytics?.byStatus?.sent || 0}</Tag>
            </div>

            <AppContextMenu
                open={!!rowMenu}
                position={rowMenu ? { x: rowMenu.x, y: rowMenu.y } : null}
                onClose={() => setRowMenu(null)}
                items={!rowMenu ? [] : [
                    {
                        key: 'view',
                        icon: <EyeOutlined />,
                        label: 'View',
                        onClick: async () => {
                            openView(rowMenu.record);
                        },
                    },
                    {
                        key: 'edit',
                        icon: <EditOutlined />,
                        label: 'Edit',
                        hidden: resolveNoticeStatus(rowMenu.record).label !== 'Scheduled',
                        onClick: async () => {
                            openEdit(rowMenu.record);
                        },
                    },
                    {
                        key: 'send',
                        icon: <NotificationOutlined />,
                        loading: menuActionLoading === 'send',
                        disabled: menuActionLoading === 'send',
                        label: (() => {
                            const status = resolveNoticeStatus(rowMenu.record).label;
                            const typeLabel = rowMenu.record?.type === 'notice' ? 'Notice' : 'Announcement';
                            if (menuActionLoading === 'send') {
                                return status === 'Sent'
                                    ? `Sending ${typeLabel} Reminder...`
                                    : `Sending ${typeLabel}...`;
                            }
                            if (status === 'Sent') return `Send ${typeLabel} Reminder`;
                            if (status === 'Scheduled') return `Send ${typeLabel} Notification Now`;
                            return `Send ${typeLabel}`;
                        })(),
                        onClick: async () => {
                            setMenuActionLoading('send');
                            await handleSendNotification(rowMenu.record);
                        },
                    },
                    {
                        key: 'delete',
                        icon: <DeleteOutlined />,
                        label: 'Delete',
                        danger: true,
                        onClick: async () => {
                            Modal.confirm({
                                title: 'Delete this notice?',
                                content: 'This action cannot be undone.',
                                okText: 'Delete',
                                okButtonProps: { danger: true },
                                onOk: () => handleDelete(rowMenu.record),
                            });
                        },
                    },
                ]}
            />

            <AppModal
                title={(
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                        <span>
                            {mode === 'create'
                                ? 'Create Notice / Announcement'
                                : mode === 'view'
                                    ? 'View Notice / Announcement'
                                    : 'Edit Notice / Announcement'}
                        </span>
                    </div>
                )}
                open={modalOpen}
                onCancel={resetModal}
                onOk={mode === 'view' ? resetModal : handleSubmit}
                confirmLoading={mode === 'view' ? false : (createMutation.isPending || updateMutation.isPending)}
                okText={mode === 'view' ? 'Close' : (mode === 'create' ? 'Create' : 'Update')}
                cancelText={mode === 'view' ? 'Close' : 'Cancel'}
                footer={mode === 'view' ? (
                    <Space>
                        {resolveNoticeStatus(editingRecord || {}).label === 'Scheduled' ? (
                            <Button
                                type="button"
                                $primary={false}
                                icon={<EditOutlined />}
                                onClick={() => {
                                    if (editingRecord?.id) openEdit(editingRecord);
                                }}
                            >
                                Edit
                            </Button>
                        ) : null}
                        <Button type="button" onClick={resetModal}>
                            Close
                        </Button>
                    </Space>
                ) : undefined}
                cancelButtonProps={mode === 'view' ? { style: { display: 'none' } } : undefined}
                size="lg"
                width="72vw"
                style={{ maxWidth: 900 }}
                styles={{
                    body: {
                        maxHeight: '70vh',
                        overflowY: 'auto',
                        paddingRight: 12,
                    },
                }}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        type: 'announcement',
                        audiences: [],
                        active: true,
                        sendImmediately: true,
                    }}
                >
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item
                            name="type"
                            label="Communication Type"
                            rules={[{ required: true, message: 'Type is required' }]}
                            style={{ flex: 1 }}
                        >
                            <AppSelect options={TYPE_OPTIONS} disabled={isViewMode} />
                        </Form.Item>

                        <Form.Item
                            name="title"
                            label="Title"
                            rules={[{ required: true, message: 'Title is required' }]}
                            style={{ flex: 1 }}
                        >
                            <AppInput placeholder="Enter title" readOnly={isViewMode} />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="body"
                        label="Message"
                        rules={[{ required: true, message: 'Message is required' }]}
                    >
                        <AppTextArea rows={5} placeholder="Write your notice or announcement..." readOnly={isViewMode} />
                    </Form.Item>

                    {isCreateMode ? (
                        <Form.Item
                            name="sendImmediately"
                            label="Send Immediately"
                            valuePropName="checked"
                            style={{ marginBottom: 8 }}
                        >
                            <Switch />
                        </Form.Item>
                    ) : null}

                    {!isViewMode ? (
                        <div
                            style={{
                                border: '1px dashed rgba(138, 152, 167, 0.55)',
                                borderRadius: 12,
                                padding: 14,
                                marginBottom: 16,
                                background: 'rgba(138, 152, 167, 0.06)',
                            }}
                        >
                            <div style={{ fontWeight: 700, marginBottom: 10 }}>Add Audience</div>
                            <Form.Item label="Target Audience" style={{ marginBottom: 10 }}>
                                <div style={{ display: 'flex', gap: 10, alignItems: 'stretch' }}>
                                    <div style={{ flex: 1, height: 44 }}>
                                        <AppSelect
                                            value={audienceTypePicker}
                                            onChange={(value) => {
                                                setAudienceTypePicker(value);
                                                setSelectedAudienceUserIds([]);
                                                setSelectedAudienceUserPicker(null);
                                                setHoveredAudienceChipId(null);
                                            }}
                                            options={availableAudienceOptions}
                                            placeholder="Choose audience type"
                                            style={{ width: '100%' }}
                                        />
                                    </div>
                                    <div style={{ height: 44, display: 'flex', alignItems: 'center' }}>
                                        <Button
                                            type="button"
                                            $primary={false}
                                            $padding="0 16px"
                                            style={{ height: 44, minHeight: 44, marginTop: 0, lineHeight: '42px' }}
                                            onClick={addAudience}
                                        >
                                            Add Audience
                                        </Button>
                                    </div>
                                </div>
                            </Form.Item>

                            {audienceTypePicker?.startsWith('selected_') ? (
                                <Form.Item label="Select Users" style={{ marginBottom: 10 }}>
                                    <AppSelect
                                        value={selectedAudienceUserPicker}
                                        onChange={(value) => {
                                            if (!value) return;
                                            setSelectedAudienceUserIds((prev) => {
                                                if (prev.includes(value)) return prev;
                                                return [...prev, value];
                                            });
                                            setSelectedAudienceUserPicker(null);
                                        }}
                                        options={filteredAudienceUserOptions}
                                        placeholder="Choose user"
                                        showSearch
                                        optionFilterProp="label"
                                    />
                                    {selectedAudienceUserChips.length ? (
                                        <div style={{ marginBottom: 10, marginTop: 10 }}>
                                            <Text type="secondary" style={{ fontSize: 12 }}>
                                                {selectedAudienceChipTitle}
                                            </Text>
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 8,
                                                }}
                                            >
                                                {selectedAudienceUserChips.map((chip) => (
                                                    <div
                                                        key={chip.id}
                                                        onMouseEnter={() => setHoveredAudienceChipId(chip.id)}
                                                        onMouseLeave={() => setHoveredAudienceChipId(null)}
                                                        style={{
                                                            border: '1px solid rgba(138, 152, 167, 0.45)',
                                                            borderRadius: 999,
                                                            padding: '5px 10px',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            gap: 8,
                                                            background: '#fff',
                                                        }}
                                                    >
                                                        <span>{chip.label}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSelectedAudienceUserIds((prev) => prev.filter((id) => id !== chip.id));
                                                            }}
                                                            style={{
                                                                border: 'none',
                                                                background: 'transparent',
                                                                cursor: 'pointer',
                                                                opacity: hoveredAudienceChipId === chip.id ? 1 : 0,
                                                                width: hoveredAudienceChipId === chip.id ? 16 : 0,
                                                                overflow: 'hidden',
                                                                transition: 'opacity 0.2s ease, width 0.2s ease',
                                                                padding: 0,
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                            }}
                                                            className="audience-chip-remove"
                                                            aria-label={`Remove ${chip.label}`}
                                                        >
                                                            <CloseOutlined />
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                    ) : null}
                                </Form.Item>
                            ) : null}
                        </div>
                    ) : null}

                    <Form.Item
                        name="audiences"
                        label="Audience List"
                        rules={[
                            {
                                validator: (_, value) => {
                                    if (Array.isArray(value) && value.length > 0) return Promise.resolve();
                                    return Promise.reject(new Error('Add at least one audience'));
                                },
                            },
                        ]}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {audiencesWatch.length === 0 ? (
                                <Text type="secondary">No audience added yet.</Text>
                            ) : (
                                audiencesWatch.map((entry, idx) => (
                                    <div
                                        key={`${entry.type}-${idx}`}
                                        style={{
                                            border: '1px solid rgba(138, 152, 167, 0.25)',
                                            borderRadius: 12,
                                            padding: '12px 14px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            gap: 12,
                                            background: '#fff',
                                        }}
                                    >
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                                            <Text style={{ fontSize: 14, color: '#1f2937' }}>
                                                {AUDIENCE_LABEL_BY_TYPE[entry.type] || entry.type}
                                            </Text>
                                            {entry.type.startsWith('selected_') ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {(entry.userIds || []).length} selected
                                                </Text>
                                            ) : (
                                                <Text type="secondary" style={{ fontSize: 12 }}>
                                                    {(entry.userIds || []).length ? `${(entry.userIds || []).length} recipients` : 'All recipients'}
                                                </Text>
                                            )}
                                        </div>
                                        {isViewMode ? null : (
                                            <Button type="button" $primary={false} danger onClick={() => removeAudience(idx)}>
                                                Remove
                                            </Button>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    </Form.Item>

                    {(isCreateMode && sendImmediatelyWatch) ? null : (
                        <Space style={{ width: '100%' }} direction="vertical">
                            {isViewMode ? null : (
                                <>
                                    <Form.Item label="Schedule Date(s)">
                                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                                            <DatePicker
                                                style={{ flex: 1 }}
                                                value={scheduleDatePicker}
                                                onChange={setScheduleDatePicker}
                                                disabled={isViewMode}
                                                disabledDate={(current) => current && !current.isAfter(dayjs().endOf('day'))}
                                            />
                                            <Button
                                                type="button"
                                                $primary={false}
                                                onClick={() => {
                                                    if (!scheduleDatePicker) {
                                                        message.warning('Please choose a schedule date');
                                                        return;
                                                    }
                                                    const next = normalizeScheduleDates([
                                                        ...scheduleDatesWatch,
                                                        scheduleDatePicker,
                                                    ]);
                                                    form.setFieldValue('scheduleDates', next);
                                                    setScheduleDatePicker(null);
                                                }}
                                            >
                                                Add Date
                                            </Button>
                                        </div>
                                    </Form.Item>
                                    <Form.Item
                                        name="scheduleDates"
                                        label="Scheduled Dates"
                                        rules={[{
                                            validator: (_, value) => {
                                                if (isCreateMode && sendImmediatelyWatch) return Promise.resolve();
                                                if (Array.isArray(value) && value.length > 0) return Promise.resolve();
                                                return Promise.reject(new Error('Add at least one schedule date'));
                                            },
                                        }]}
                                    >
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {scheduleDatesWatch.length === 0 ? (
                                                <Text type="secondary">No schedule dates added yet.</Text>
                                            ) : (
                                                scheduleDatesWatch.map((dt, idx) => (
                                                    <Tag
                                                        key={`${dayjs(dt).valueOf()}-${idx}`}
                                                        closable={!isViewMode}
                                                        onClose={(e) => {
                                                            e.preventDefault();
                                                            form.setFieldValue(
                                                                'scheduleDates',
                                                                scheduleDatesWatch.filter((_, i) => i !== idx)
                                                            );
                                                        }}
                                                    >
                                                        {formatDate(dt)}
                                                    </Tag>
                                                ))
                                            )}
                                        </div>
                                    </Form.Item>
                                </>
                            )}
                        </Space>
                    )}

                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Immediate sends now. Scheduled announcements send when publish date becomes due.
                    </Text>
                </Form>
                {editingRecord?.id ? (
                    <div
                        style={{
                            marginTop: 20,
                            borderTop: '1px solid rgba(15, 23, 42, 0.08)',
                            paddingTop: 14,
                        }}
                    >
                        <div style={{ fontWeight: 700, marginBottom: 10 }}>All Schedule Dates</div>
                        {detailScheduleDates.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
                                {detailScheduleDates.map((dt, idx) => (
                                    <Tag key={`${dayjs(dt).valueOf()}-${idx}`}>{formatDate(dt)}</Tag>
                                ))}
                            </div>
                        ) : (
                            <Text type="secondary" style={{ fontSize: 13, display: 'block', marginBottom: 14 }}>
                                No schedule dates.
                            </Text>
                        )}
                        <div style={{ fontWeight: 700, marginBottom: 10 }}>Notification History</div>
                        {notificationHistoryRows.length > 0 ? (
                            <Table
                                title=""
                                columns={[
                                    { title: 'When', dataIndex: 'when', key: 'when' },
                                    { title: 'By', dataIndex: 'by', key: 'by' },
                                    { title: 'Type', dataIndex: 'type', key: 'type' },
                                    { title: 'Source', dataIndex: 'source', key: 'source' },
                                ]}
                                dataSource={notificationHistoryRows}
                                showSearch={false}
                                showFilter={false}
                                showMore={false}
                                pagination={false}
                                $topMargin={false}
                            />
                        ) : (
                            <Text type="secondary" style={{ fontSize: 13 }}>
                                No notification history yet.
                            </Text>
                        )}
                    </div>
                ) : null}
            </AppModal>
        </DashboardContainer>
    );
};

export default NoticesAnnouncements;
